import { FileStatus } from '../core/types';

/**
 * Whether the files a review would send are ready for CENTERLINE AI to read (plan §4.3). The Query
 * Tool drops, without a word, any file it has not indexed yet, so a contractor's submission uploaded
 * minutes ago would be quietly ignored; this is how the pane says so first.
 */

export const WAIT_INTERVAL_MS = 30_000;
export const WAIT_MAX_MS = 10 * 60_000;

export interface NamedFile {
  id: string;
  name: string;
}

export interface PendingFile extends NamedFile {
  stage: FileStatus['stage'];
}

export type ReadinessVerdict =
  /** The Query Tool could not say (an older deploy, or an error): send everything. */
  | 'unknown'
  | 'all_ready'
  /** Some are ready, or there are no files of the document's own to wait for. */
  | 'partial'
  /** None of the document's own files is ready: do not send on our own. */
  | 'none_ready';

export interface ReadinessReport {
  verdict: ReadinessVerdict;
  readyIds: string[];
  /** Still on their way: queued, parsing, indexing or not yet queued. */
  pending: PendingFile[];
  /** Never going to be readable: failed or an unsupported type. */
  unreadable: PendingFile[];
}

const UNREADABLE: FileStatus['stage'][] = ['failed', 'unsupported'];

export function assessReadiness(
  files: NamedFile[],
  ownFileIds: string[],
  statuses: FileStatus[] | null,
): ReadinessReport {
  if (statuses === null) {
    return { verdict: 'unknown', readyIds: files.map((f) => f.id), pending: [], unreadable: [] };
  }
  const byId = new Map(statuses.map((s) => [s.file_id, s]));
  const readyIds: string[] = [];
  const pending: PendingFile[] = [];
  const unreadable: PendingFile[] = [];
  files.forEach((f) => {
    const status = byId.get(f.id);
    if (status?.indexed) readyIds.push(f.id);
    else if (status && UNREADABLE.includes(status.stage)) {
      unreadable.push({ ...f, stage: status.stage });
    } else pending.push({ ...f, stage: status?.stage ?? 'not_queued' });
  });
  if (!pending.length && !unreadable.length) {
    return { verdict: 'all_ready', readyIds, pending, unreadable };
  }
  const ownReady = ownFileIds.filter((id) => readyIds.includes(id)).length;
  const verdict = ownFileIds.length > 0 && ownReady === 0 ? 'none_ready' : 'partial';
  return { verdict, readyIds, pending, unreadable };
}

const names = (files: NamedFile[]) => files.map((f) => f.name).join(', ');

const REASON: Partial<Record<FileStatus['stage'], string>> = {
  failed: 'could not be processed',
  unsupported: 'unsupported type',
};

/** What the pane says about files that are not ready; empty when there is nothing to say. */
export function readinessNotes(report: ReadinessReport, total: number): string[] {
  const notes: string[] = [];
  if (report.pending.length) {
    notes.push(
      `${report.pending.length} of ${total} files ${
        report.pending.length === 1 ? 'is' : 'are'
      } still being processed by CENTERLINE AI (usually a few minutes). The review will not include: ${names(
        report.pending,
      )}.`,
    );
  }
  if (report.unreadable.length) {
    notes.push(
      `CENTERLINE AI can't read: ${report.unreadable
        .map((f) => `${f.name} (${REASON[f.stage] ?? f.stage})`)
        .join(', ')}.`,
    );
  }
  return notes;
}
